import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Calendar, MapPin, ArrowRight } from "lucide-react";

const statusStyle = (s) =>
  s === "confirmed" ? "text-white border-white/40 bg-white/10"
  : s === "completed" ? "text-white/70 border-white/20"
  : s === "cancelled" ? "text-white/40 border-white/10"
  : "text-muted-foreground border-white/15";

export default function Account() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bookings/mine")
      .then(({ data }) => setBookings(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-luxe text-muted-foreground mb-3">{t("account.welcome")}</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-white">{user?.name || t("nav.myAccount")}</h1>
            <p className="text-muted-foreground mt-2">{user?.email}</p>
          </div>
          <Button data-testid="account-new-booking-btn"
            className="rounded-full bg-white text-black hover:bg-white/85 px-7"
            onClick={() => navigate("/book")}>
            <Plus className="h-4 w-4 mr-2" /> {t("account.newBooking")}
          </Button>
        </div>

        <h2 className="text-white text-lg mb-6">{t("account.yourReservations")}</h2>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>
        ) : bookings.length === 0 ? (
          <div className="rounded-lg border border-white/10 bg-card p-16 text-center">
            <p className="text-muted-foreground mb-6">{t("account.empty")}</p>
            <Button className="rounded-full bg-white text-black hover:bg-white/85" onClick={() => navigate("/book")}>
              {t("account.first")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`}
                className="rounded-lg border border-white/10 bg-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-white/30 transition-colors">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-serif text-xl text-white">{b.service_label}</h3>
                    <span className={`text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full border ${statusStyle(b.status)}`}>
                      {b.status}
                    </span>
                    {b.payment_status === "deposit_paid" && (
                      <span className="text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full border border-white/20 text-white/70">
                        {t("account.depositPaid")}
                      </span>
                    )}
                    {(b.quote_required || b.payment_status === "quote_requested") && (
                      <span className="text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full border border-white/20 text-white/70">
                        {t("account.quoteRequested")}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> {b.date} · {b.time}</span>
                    <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {b.pickup_location} → {b.dropoff_location}</span>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    {b.estimated_price != null ? (
                      <>
                        <p className="text-white font-serif text-xl">${b.estimated_price}</p>
                        <p className="text-xs text-muted-foreground">{t("account.deposit")} ${b.deposit_amount}</p>
                      </>
                    ) : (
                      <p className="text-white/70 text-sm">{t("account.priceOnRequest")}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full"
                    data-testid={`view-booking-${b.id}`}
                    onClick={() => navigate(`/booking/${b.id}`)}>
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
